import { useEffect, useRef } from 'react';
import { useGetUserQuery } from 'src/common/slices';

/**
 * When a user transitions from logged out to logged in, runs a callback once.
 *
 * Does **not** run if user is already logged in during initial web-app load.
 */
export const useOnUserLogin = (callback: () => void) => {
  const { data, isLoading } = useGetUserQuery();
  const previousAuthStateRef = useRef<boolean | null>(null);
  const hasRunRef = useRef(false);
  const callbackRef = useRef(callback);

  /** Keep callback ref up to date */
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const isCurrentlyAuthenticated = !!data?.data?.user;

  useEffect(() => {
    if (hasRunRef.current) return;

    if (isLoading) return;

    if (previousAuthStateRef.current === false && isCurrentlyAuthenticated) {
      hasRunRef.current = true;
      callbackRef.current();
    }

    previousAuthStateRef.current = isCurrentlyAuthenticated;
  }, [isLoading, isCurrentlyAuthenticated]);
};
